import React, { useMemo, useState, useEffect, useContext, useRef } from "react";
import { ContextEngine } from "../engine/ContextEngine";
import { useDocSave } from "../engine/hooks/useDocSave";

const SheetSettingForm = ({ closeModal }) => {
  const { context } = useContext(ContextEngine);
  const { mutate: saveDoc, isLoading } = useDocSave();

  // 1. Local State for editing and selection
  const tableId = "defaultTable";
  const unit = context.context_units.find((u) => u.id === tableId);
  const [localData, setLocalData] = useState(unit?.data || []);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRefs = useRef([]);
  const modalRef = useRef(null);

  // 3. Selection & Edit Handlers
  const toggleSelect = (index) => {
    const newData = [...localData];
    newData[index].meta.selected = !newData[index].meta.selected;
    setLocalData(newData);
  };

  const handleEdit = (index, value) => {
    const newData = [...localData];
    newData[index].row.Label = value; // Example editable field
    setLocalData(newData);
  };

  const onAccept = () => {
    const selectedRows = localData.filter(r => r.meta.selected);
    const target = selectedRows.length > 0 ? selectedRows : [localData[activeIndex]];

    // If multi-select is active, you might loop or send a bulk update
    target.forEach(row => {
      saveDoc({ 
        rowData: row, 
        projectId: context.view.selectedProject, 
        section: context.view.selectedSection 
      });
    });
    closeModal();
  };
  const [lastClickedIndex, setLastClickedIndex] = useState(null);

  const handleRowClick = (index, event) => {
    // 🛑 STOP the background table from seeing this click
    event.stopPropagation();
    if (event.nativeEvent) event.nativeEvent.stopImmediatePropagation();

    const newData = [...localData];
    
    if (event.shiftKey && lastClickedIndex !== null) {
      const start = Math.min(lastClickedIndex, index);
      const end = Math.max(lastClickedIndex, index);
      for (let i = start; i <= end; i++) {
        newData[i].meta.selected = true;
      }
    } else {
      newData[index].meta.selected = !newData[index].meta.selected;
      setLastClickedIndex(index);
    }
    
    setLocalData(newData);
    setActiveIndex(index);
  };

  

  // 2. Automatically focus the input of the active row when index changes
  useEffect(() => {
    const currentInput = inputRefs.current[activeIndex];
    if (currentInput) {
      currentInput.focus();
      // Optional: select the text so user can overwrite immediately
      currentInput.select(); 
    }
  }, [activeIndex]);

  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden"; // Prevents background scroll/arrow-key move
    return () => (document.body.style.overflow = originalStyle);
  }, []);

  const handleKeyDown = (e) => {
    // 🛑 CRITICAL: Stop background window from hearing this
    e.stopPropagation();

    if (e.key === "ArrowDown") {
      e.preventDefault();
      const nextIndex = Math.min(activeIndex + 1, localData.length - 1);
      if (e.shiftKey) toggleSelect(nextIndex, true);
      setActiveIndex(nextIndex);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const nextIndex = Math.max(activeIndex - 1, 0);
      if (e.shiftKey) toggleSelect(nextIndex, true);
      setActiveIndex(nextIndex);
    }
  };

  // Auto-focus the modal on mount so it catches the first keypress
useEffect(() => {
  if (modalRef.current) {
    modalRef.current.focus();
  }
}, []);

useEffect(() => {
  const handleCaptureKeyDown = (e) => {
    if (["ArrowUp", "ArrowDown", "Tab", " "].includes(e.key)) {
      // 🛑 Intercept and kill the event before background sees it
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      // Run your modal logic
      handleKeyDown(e); 
    }
  };

  // The 'true' here is the "Capture" flag—it makes the modal the priority
  window.addEventListener("keydown", handleCaptureKeyDown, true); 
  return () => window.removeEventListener("keydown", handleCaptureKeyDown, true);
}, [activeIndex, localData]);

useEffect(() => {
  document.body.style.userSelect = "none"; // Stop background text highlighting
  return () => { document.body.style.userSelect = "auto"; };
}, []);


  return (
    <div style={styles.overlay}
    onClick={closeModal} // Clicking the dark backdrop closes modal
    >
      <div
        ref={modalRef}
        style={styles.modal}
        tabIndex="0" // 2. Makes the div focusable
        onKeyDown={handleKeyDown} // 3. Injects the key listener here
        onClick={(e) => e.stopPropagation()} // 4. Prevents backdrop-click from closing modal when clicking inside
      >
        <div style={styles.header}>
          <h2 style={{ margin: 0, fontSize: '1.2rem' }}>Sheet Settings</h2>
          <span style={{fontSize: '10px', color: '#888'}}>↑↓ to navigate • Space to select</span>
          <button onClick={closeModal} style={styles.closeBtn}>&times;</button>
        </div>

        <div style={styles.body}>
          <div style={styles.infoPanel}>
            <h3 style={styles.panelTitle}>Stats</h3>
            <div style={styles.statItem}><span>Items:</span><strong>{localData.length}</strong></div>
            <div style={styles.statItem}><span>Selected:</span><strong>{localData.filter(r => r.meta.selected).length}</strong></div>
          </div>

          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{width: '30px'}}>#</th>
                  <th>Label (Editable)</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {localData.map((row, i) => {
                  const isActive = i === activeIndex;
                  const isSelected = row.meta?.selected;

                  return (
                    <tr 
                      key={row.row_id} 
                      onClick={(e) => handleRowClick(i, e)} // Pass the event for shiftKey check
                      style={{ 
                        backgroundColor: isActive ? "#2c3e50" : isSelected ? "#1a252f" : "transparent",
                        borderLeft: isSelected ? "4px solid #007bff" : "4px solid transparent",
                        outline: isActive ? "1px solid #555" : "none", // Makes active row pop
                        cursor: 'pointer'
                      }}
                    >
                      <td style={{padding: '8px', color: '#666'}}>{i + 1}</td>
                      <td>
                        <input 
                          ref={el => inputRefs.current[i] = el} // Store ref for this specific row
                          value={row.row.Label || ""} 
                          onChange={(e) => handleEdit(i, e.target.value)}
                          onClick={(e) => e.stopPropagation()} // Prevents row click when typing
                          style={styles.inlineInput}
                        />
                      </td>
                      <td>{row.row_id}</td>
                    </tr>
                  );
                })}

              </tbody>
            </table>
          </div>
        </div>

        <div style={styles.footer}>
          <button onClick={closeModal} style={styles.cancelBtn}>Close</button>
          <button onClick={onAccept} style={styles.saveBtn} disabled={isLoading}>
            {isLoading ? "Saving..." : `Save ${localData.filter(r => r.meta.selected).length || 1} Row(s)`}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  // ... your existing styles ...
  inlineInput: {
    background: 'transparent',
    border: 'none',
    color: 'white',
    width: '100%',
    padding: '4px',
    outline: 'none',
    borderBottom: '1px solid transparent',
    transition: 'border 0.2s',
  },
  // Add focus style for input
  overlay: { position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0,0,0,0.7)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
  modal: { width: "750px", height: "500px", background: "#1c1c1c", borderRadius: "12px", display: "flex", flexDirection: "column", overflow: "hidden", color: "#fff", border: "1px solid #444" },
  header: { padding: "15px 20px", borderBottom: "1px solid #333", display: "flex", justifyContent: "space-between", alignItems: "center" },
  body: { flex: 1, display: "flex", overflow: "hidden" },
  infoPanel: { width: "200px", borderRight: "1px solid #333", padding: "20px", background: "#252525" },
  tableContainer: { flex: 1, padding: "10px", overflowY: "auto", background: "#111" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "13px" },
  footer: { padding: "15px 20px", borderTop: "1px solid #333", display: "flex", justifyContent: "flex-end", gap: "10px" },
  badge: { background: '#2e7d32', padding: '2px 6px', borderRadius: '4px', fontSize: '10px' },
  cancelBtn: { padding: "8px 16px", borderRadius: "4px", border: "1px solid #555", background: "transparent", color: "#fff", cursor: "pointer" },
  saveBtn: { padding: "8px 16px", borderRadius: "4px", border: "none", background: "#007bff", color: "#fff", cursor: "pointer" },
  closeBtn: { background: 'none', border: 'none', color: '#fff', fontSize: '24px', cursor: 'pointer' },
  panelTitle: { fontSize: '12px', color: '#888', marginBottom: '10px' },
  statItem: { display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }
};

export default SheetSettingForm;
